import React, { useState, useEffect } from 'react';
import { devCenter } from '../../services/api';
import { ShieldAlert, Activity, GitBranch, GitMerge, FileText, CheckCircle, Clock,
         AlertTriangle, Play, Check, ChevronRight, Zap, Package, Eye, XCircle,
         ArrowRight, Shield, Sun, ListChecks } from 'lucide-react';
import WorkItemsManager from './WorkItemsManager';
import SessionLogViewer from './SessionLogViewer';
import TeamAccessManager from './TeamAccessManager';

const DevCommandCenter = ({ onBack }) => {
    const [access, setAccess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [todayData, setTodayData] = useState(null);
    const [activeTab, setActiveTab] = useState('briefing');

    useEffect(() => {
        const init = async () => {
            try {
                const res = await devCenter.getAccess();
                if (res.data.authorized) {
                    setAccess(res.data);
                    fetchToday();
                } else {
                    setError("Access Denied");
                    setLoading(false);
                }
            } catch (err) {
                console.error(err);
                setError("Failed to verify access.");
                setLoading(false);
            }
        };
        init();
    }, []);

    const fetchToday = async () => {
        try {
            const res = await devCenter.getToday();
            setTodayData(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-10 flex justify-center text-slate-500">Checking credentials...</div>;
    
    if (error || !access) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-slate-200 p-6">
                <ShieldAlert size={48} className="text-red-500 mb-4" />
                <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
                <p className="text-slate-400 mb-8 max-w-md text-center">
                    You do not have permission to access the Development Command Center.
                </p>
                <button onClick={onBack} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">
                    Back to Dashboard
                </button>
            </div>
        );
    }

    const tabs = [
        { id: 'briefing', label: 'Daily Brief', icon: Sun },
        { id: 'items', label: 'Work Items', icon: ListChecks },
        { id: 'sessions', label: 'Sessions', icon: FileText },
        ...(access.role === 'admin' ? [{ id: 'team', label: 'Team', icon: Shield }] : [])
    ];

    return (
        <div className="min-h-screen bg-slate-900 text-slate-300 font-sans flex flex-col">
            <header className="bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-1.5 rounded-lg text-white">
                        <Activity size={20} />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-white leading-tight">Dev Command Center</h1>
                        <p className="text-xs text-slate-400 capitalize">Role: {access.role}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                                activeTab === tab.id
                                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                            }`}
                        >
                            <tab.icon size={14} />
                            {tab.label}
                        </button>
                    ))}
                    <div className="w-px h-6 bg-slate-700 mx-2" />
                    <button onClick={onBack} className="text-sm text-slate-400 hover:text-white transition-colors">
                        Exit
                    </button>
                </div>
            </header>

            <main className="flex-1 p-6 max-w-7xl mx-auto w-full">
                {activeTab === 'briefing' && <DailyBriefing data={todayData} access={access} onRefresh={fetchToday} />}
                {activeTab === 'items' && <WorkItemsManager />}
                {activeTab === 'sessions' && <SessionLogViewer />}
                {activeTab === 'team' && access.role === 'admin' && <TeamAccessManager />}
            </main>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
// DAILY BRIEFING — The Morning Command Center
// ═══════════════════════════════════════════════════════════

const DailyBriefing = ({ data, access, onRefresh }) => {
    if (!data) return <div className="text-slate-500 text-center py-10">Loading briefing...</div>;

    const prodToday = data.production_deployed_today;

    return (
        <div className="space-y-6">
            {/* ── TOP STATS ROW ── */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard
                    label="Open Items"
                    value={data.total_open_items || 0}
                    icon={Package}
                    color="text-blue-400"
                />
                <StatCard
                    label="On Staging"
                    value={data.current_staging?.length || 0}
                    icon={GitBranch}
                    color="text-cyan-400"
                />
                <StatCard
                    label="Needs Testing"
                    value={data.waiting_for_testing?.length || 0}
                    icon={Eye}
                    color="text-amber-400"
                />
                <StatCard
                    label="Blockers"
                    value={data.blockers?.length || 0}
                    icon={XCircle}
                    color={data.blockers?.length > 0 ? "text-red-400" : "text-slate-500"}
                />
                <StatCard
                    label="Prod Today"
                    value={prodToday ? "✓ Deployed" : "No Deploy"}
                    icon={GitMerge}
                    color={prodToday ? "text-emerald-400" : "text-slate-500"}
                    small
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ── LEFT COLUMN: Briefing Panels ── */}
                <div className="lg:col-span-2 space-y-5">

                    {/* YESTERDAY */}
                    <BriefingSection
                        title="Yesterday"
                        icon={Clock}
                        color="text-purple-400"
                        items={data.yesterday_completed}
                        emptyText="No changes completed yesterday."
                        renderItem={(item) => (
                            <ItemRow key={item.id} item={item} />
                        )}
                    />

                    {/* STAGING NOW */}
                    <BriefingSection
                        title="Staging Now"
                        icon={GitBranch}
                        color="text-cyan-400"
                        items={data.current_staging}
                        emptyText="Nothing on staging right now."
                        renderItem={(item) => (
                            <ItemRow key={item.id} item={item} />
                        )}
                    />

                    {/* PRODUCTION NOW */}
                    <BriefingSection
                        title="Production Now"
                        icon={GitMerge}
                        color="text-emerald-400"
                        items={data.current_production}
                        emptyText="No items in the production pipeline."
                        renderItem={(item) => (
                            <ItemRow key={item.id} item={item} />
                        )}
                    />

                    {/* NEEDS TESTING */}
                    {data.waiting_for_testing?.length > 0 && (
                        <BriefingSection
                            title="Needs Testing"
                            icon={Eye}
                            color="text-amber-400"
                            items={data.waiting_for_testing}
                            renderItem={(item) => (
                                <ItemRow key={item.id} item={item} />
                            )}
                        />
                    )}

                    {/* BLOCKERS */}
                    {data.blockers?.length > 0 && (
                        <BriefingSection
                            title="Blockers"
                            icon={AlertTriangle}
                            color="text-red-400"
                            items={data.blockers}
                            renderItem={(item) => (
                                <ItemRow key={item.id} item={item} highlight="red" />
                            )}
                        />
                    )}

                    {/* RECENT SESSIONS */}
                    <section>
                        <div className="flex items-center gap-2 mb-3">
                            <FileText size={16} className="text-slate-400" />
                            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Recent Sessions</h3>
                        </div>
                        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl overflow-hidden">
                            {data.recent_sessions?.length > 0 ? (
                                <div className="divide-y divide-slate-700/40">
                                    {data.recent_sessions.slice(0, 5).map(s => (
                                        <div key={s.id} className="p-3 hover:bg-slate-700/20 transition-colors">
                                            <div className="text-sm font-medium text-white mb-0.5 truncate">{s.objective}</div>
                                            <div className="text-xs text-slate-400 truncate">{s.exact_change_summary}</div>
                                            <div className="flex gap-2 mt-1.5">
                                                <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                                                    {s.source}
                                                </span>
                                                <span className="text-[10px] text-slate-500">
                                                    {new Date(s.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-6 text-center text-sm text-slate-500">No recent sessions.</div>
                            )}
                        </div>
                    </section>
                </div>

                {/* ── RIGHT COLUMN: Actions & Intelligence ── */}
                <div className="space-y-5">

                    {/* NEXT ACTIONS */}
                    <section className="bg-gradient-to-b from-blue-950/40 to-slate-800/60 border border-blue-800/30 rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                            <Zap size={16} className="text-amber-400" />
                            Next Actions
                        </h3>
                        {data.recommended_next_actions?.length > 0 ? (
                            <div className="space-y-2.5">
                                {data.recommended_next_actions.slice(0, 8).map((rec, i) => (
                                    <div key={i} className="flex items-start gap-2.5">
                                        <PriorityDot priority={rec.priority} />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-white leading-snug">{rec.action}</div>
                                            <div className="text-[11px] text-slate-400 mt-0.5">{rec.reason}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500 italic">All clear — no pending actions.</div>
                        )}
                    </section>

                    {/* DEPLOYMENT GUARD */}
                    {data.deployment_guard_status && (
                        <section className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
                            <h3 className="text-white font-medium mb-3 flex items-center gap-2 text-sm">
                                <Shield size={16} className="text-slate-400" />
                                Deployment Guard
                            </h3>
                            <div className="space-y-2">
                                {data.deployment_guard_status.required_checks.map(chk => (
                                    <div key={chk.id} className="flex items-center gap-2 text-sm">
                                        {chk.status === "pass" ? (
                                            <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
                                        ) : chk.status === "fail" ? (
                                            <XCircle size={14} className="text-red-400 flex-shrink-0" />
                                        ) : (
                                            <div className="w-3.5 h-3.5 border border-slate-500 rounded-full flex-shrink-0" />
                                        )}
                                        <span className={chk.status === "pass" ? "text-slate-300" : "text-slate-500"}>
                                            {chk.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ADMIN ACTIONS */}
                    {access.role === 'admin' && (
                        <section className="space-y-2.5">
                            <button onClick={async () => {
                                try {
                                    await devCenter.generateDailySummary();
                                    alert("Daily summary generated!");
                                    onRefresh();
                                } catch (e) {
                                    alert(e.response?.data?.message || e.response?.data?.detail || "Failed to generate summary");
                                }
                            }} className="w-full text-left px-4 py-3 bg-slate-800 border border-slate-700 hover:border-slate-500 rounded-xl text-sm text-white transition-all flex items-center justify-between group">
                                <span className="flex items-center gap-2">
                                    <Check size={16} className="text-slate-500 group-hover:text-emerald-400 transition-colors" />
                                    Close Day / Generate Summary
                                </span>
                                <ChevronRight size={14} className="text-slate-600" />
                            </button>
                        </section>
                    )}

                    {/* PRODUCTION STATUS BADGE */}
                    <div className={`rounded-xl p-4 border text-center ${
                        prodToday
                            ? 'bg-emerald-950/30 border-emerald-800/40'
                            : 'bg-slate-800/40 border-slate-700/40'
                    }`}>
                        <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                            prodToday ? 'text-emerald-400' : 'text-slate-500'
                        }`}>
                            {prodToday ? 'Production Deployed Today' : 'No Production Deploy Today'}
                        </div>
                        {data.todays_prod_deployments?.length > 0 && (
                            <div className="text-[11px] text-slate-400 mt-1">
                                {data.todays_prod_deployments[0].batch_name} — {data.todays_prod_deployments[0].deployed_by}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════

const StatCard = ({ label, value, icon: IconComponent, color, small }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-xl bg-slate-900 shadow-inner ${color}`}>
            {IconComponent && <IconComponent size={small ? 18 : 22} />}
        </div>
        <div>
            <div className={`font-semibold text-white ${small ? 'text-sm' : 'text-xl'}`}>{value}</div>
            <div className="text-xs text-slate-400">{label}</div>
        </div>
    </div>
);

const STATUS_COLORS = {
    'Draft': 'bg-slate-600',
    'In Progress': 'bg-blue-600',
    'On Staging': 'bg-cyan-600',
    'Testing in Progress': 'bg-amber-600',
    'Staging Approved': 'bg-emerald-600',
    'Changes Requested': 'bg-orange-600',
    'Blocked': 'bg-red-600',
    'Queued for Production': 'bg-purple-600',
    'Deployed to Production': 'bg-emerald-700',
    'Verified in Production': 'bg-emerald-500',
};

const ItemRow = ({ item, highlight }) => (
    <div className={`flex items-center justify-between px-3 py-2.5 hover:bg-slate-700/20 transition-colors ${
        highlight === 'red' ? 'border-l-2 border-red-500' : ''
    }`}>
        <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate">{item.title}</div>
            <div className="flex items-center gap-2 mt-1">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold text-white ${
                    STATUS_COLORS[item.status] || 'bg-slate-600'
                }`}>
                    {item.status}
                </span>
                <span className="text-[10px] text-slate-500 uppercase">{item.type}</span>
                {item.priority && item.priority !== 'Medium' && (
                    <span className={`text-[10px] font-bold ${
                        item.priority === 'Critical' ? 'text-red-400' :
                        item.priority === 'High' ? 'text-amber-400' : 'text-slate-500'
                    }`}>
                        {item.priority}
                    </span>
                )}
            </div>
        </div>
        <ArrowRight size={14} className="text-slate-600 flex-shrink-0 ml-2" />
    </div>
);

const BriefingSection = ({ title, icon: Icon, color, items, emptyText, renderItem }) => (
    <section>
        <div className="flex items-center gap-2 mb-3">
            <Icon size={16} className={color} />
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">{title}</h3>
            {items?.length > 0 && (
                <span className="text-[10px] font-bold text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded-full ml-1">
                    {items.length}
                </span>
            )}
        </div>
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl overflow-hidden">
            {items?.length > 0 ? (
                <div className="divide-y divide-slate-700/30">
                    {items.map(renderItem)}
                </div>
            ) : (
                <div className="p-5 text-center text-sm text-slate-500 italic">{emptyText || 'None.'}</div>
            )}
        </div>
    </section>
);

const PriorityDot = ({ priority }) => {
    const colors = {
        critical: 'bg-red-500',
        high: 'bg-amber-500',
        medium: 'bg-blue-500',
        low: 'bg-slate-500'
    };
    return (
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${colors[priority] || 'bg-slate-500'}`} />
    );
};

export default DevCommandCenter;
